import { Modal } from 'antd';
import { useRequest } from '../../../../../lib/hooks';
import { maskCpf } from '../../../../../lib/utils/cpfValidator';
import { request } from '../../../../../lib/utils/request';
import {
	InsertSearchTagDto,
	SearchTagDto,
	UpdateSearchTagDto,
} from '../dto/SearchTagDto';

const BASE_URL = `${process.env.REACT_APP_SEARCH_TAGS_API}/search-tags`;

export async function findSearchTagByCpf(cpf: string): Promise<SearchTagDto | null> {
	try {
		const tag = await request().get<SearchTagDto>(`${BASE_URL}/by-cpf/${cpf}`);
		return tag || null;
	} catch (err: any) {
		if (err?.response?.status === 404) return null;
		throw err;
	}
}

export function createSearchTag(dto: InsertSearchTagDto): Promise<SearchTagDto> {
	return request().post<SearchTagDto>(`${BASE_URL}`, dto);
}

export function updateSearchTag(dto: UpdateSearchTagDto): Promise<SearchTagDto> {
	return request().put<SearchTagDto>(`${BASE_URL}/${dto.id}`, {
		title: dto.title,
		prefix: dto.prefix,
		cpf: dto.cpf,
		force: dto.force,
	});
}

export type SubmitOutcome =
	| { kind: 'ok' }
	| { kind: 'cancelled' }
	| { kind: 'error'; error: any };

export async function submitWithOverwriteConfirm<T>(
	doRequest: (dto: T) => Promise<any>,
	dto: T & { force?: boolean }
): Promise<SubmitOutcome> {
	try {
		await doRequest(dto);
		return { kind: 'ok' };
	} catch (err: any) {
		const status = err?.response?.status;
		const code = err?.response?.data?.code;
		if (status !== 409 || code !== 'name-conflict-with-cpf') {
			return { kind: 'error', error: err };
		}

		const existing = err?.response?.data?.existing;
		const cpfMasked = existing?.cpf ? maskCpf(existing.cpf) : '';
		const confirmed = await new Promise<boolean>((resolve) => {
			Modal.confirm({
				title: 'Autor já cadastrado com CPF',
				content: `Já existe um autor com o nome "${existing?.title}" cadastrado com o CPF ${cpfMasked}. Deseja sobrescrever? O CPF atual será removido.`,
				okText: 'Sobrescrever',
				cancelText: 'Cancelar',
				onOk: () => resolve(true),
				onCancel: () => resolve(false),
			});
		});

		if (!confirmed) return { kind: 'cancelled' };

		try {
			await doRequest({ ...dto, force: true });
			return { kind: 'ok' };
		} catch (retryErr) {
			return { kind: 'error', error: retryErr };
		}
	}
}

export function useSearchTagsApi() {
	const insert = useCreateTag();
	const update = useUpdateTag();
	const remove = useRemoveTag();

	return {
		insert,
		update,
		remove,
	};
}

function useCreateTag() {
	const [result, loading, error, do_create] = useRequest<SearchTagDto>(
		(dto: InsertSearchTagDto) =>
			new Promise((resolve, reject) => {
				request()
					.post(`${BASE_URL}`, dto)
					.then((res: any) => setTimeout(() => resolve(res), 1500))
					.catch(reject);
			})
	);

	return {
		create: (dto: InsertSearchTagDto) => do_create(dto),
		result,
		error,
		loading,
	};
}

function useUpdateTag() {
	const [result, loading, error, do_update] = useRequest<SearchTagDto>(
		(dto: UpdateSearchTagDto) =>
			new Promise((resolve, reject) => {
				request()
					.put(`${BASE_URL}/${dto.id}`, {
						title: dto.title,
						prefix: dto.prefix,
						cpf: dto.cpf,
						force: dto.force,
					})
					.then((res: any) => setTimeout(() => resolve(res), 1500))
					.catch(reject);
			})
	);

	return {
		do_update: (dto: UpdateSearchTagDto) => do_update(dto),
		result,
		error,
		loading,
	};
}

function useRemoveTag() {
	const [result, loading, error, remove] = useRequest<string>(
		(id: string) =>
			new Promise((resolve, reject) => {
				request()
					.delete(`${BASE_URL}/${id}`)
					.then((res: any) => setTimeout(() => resolve(res), 1500))
					.catch(reject);
			})
	);

	return {
		do_remove: (id: string) => remove(id),
		result,
		loading,
		error,
	};
}
